/**
 * URL Router - Manages URL state for session and view mode
 * Enables deep linking and refresh persistence
 */

const STORAGE_KEY = 'hex-map-session-state';
const DEMO_STORAGE_KEY = 'hex-map-demo-session-state';

export class URLRouter {
  /**
   * Get the appropriate storage for a session
   * @param {Object|string} sessionOrType - Session object or 'demo' string
   * @returns {Storage} sessionStorage for demos, localStorage for normal sessions
   */
  static getStorage(sessionOrType) {
    if (typeof sessionOrType === 'string') {
      return sessionOrType === 'demo' ? sessionStorage : localStorage;
    }
    return sessionOrType?.isDemo ? sessionStorage : localStorage;
  }

  /**
   * Get storage key based on session type
   * @param {Object|string} sessionOrType - Session object or 'demo' string
   * @returns {string} Storage key
   */
  static getStorageKey(sessionOrType) {
    const isDemo = typeof sessionOrType === 'string'
      ? sessionOrType === 'demo'
      : sessionOrType?.isDemo;
    return isDemo ? DEMO_STORAGE_KEY : STORAGE_KEY;
  }

  /**
   * Get current URL parameters
   * @returns {Object} Object with session and view properties
   */
  static getState() {
    const params = new URLSearchParams(window.location.search);
    return {
      session: params.get('session') || null,
      view: params.get('view') || 'gm'
    };
  }

  /**
   * Update URL parameters without reloading
   * @param {Object} state - State object with session and/or view
   */
  static setState(state) {
    const params = new URLSearchParams(window.location.search);

    if (state.session !== undefined) {
      if (state.session) {
        params.set('session', state.session);
      } else {
        params.delete('session');
      }
    }

    if (state.view !== undefined) {
      if (state.view === 'gm') {
        params.delete('view'); // Default is GM, no need to show in URL
      } else {
        params.set('view', state.view);
      }
    }

    const newUrl = params.toString()
      ? `${window.location.pathname}?${params.toString()}`
      : window.location.pathname;

    window.history.pushState({}, '', newUrl);
  }

  /**
   * Navigate to home (landing page)
   */
  static goHome() {
    window.location.href = '/';
  }

  /**
   * Save session state to appropriate storage (sessionStorage for demos, localStorage for normal)
   * @param {Object} session - Session object
   * @param {Object} mapData - Map data object
   */
  static saveSessionState(session, mapData) {
    if (!session) return;

    const state = {
      session: session,
      mapData: mapData,
      timestamp: Date.now()
    };

    try {
      const storage = this.getStorage(session);
      const key = this.getStorageKey(session);
      storage.setItem(key, JSON.stringify(state));
    } catch (error) {
      console.warn('Failed to save session to storage:', error);
    }
  }

  /**
   * Load session state from appropriate storage
   * @param {string} sessionType - 'demo' for sessionStorage, null/sessionId for localStorage
   * @returns {Object|null} Object with session and mapData, or null
   */
  static loadSessionState(sessionType = null) {
    try {
      const storage = this.getStorage(sessionType);
      const key = this.getStorageKey(sessionType);
      const stored = storage.getItem(key);
      if (!stored) return null;

      const state = JSON.parse(stored);

      // Check age only for localStorage (sessionStorage clears on tab close)
      if (storage === localStorage) {
        const maxAge = 7 * 24 * 60 * 60 * 1000; // 7 days
        if (Date.now() - state.timestamp > maxAge) {
          storage.removeItem(key);
          return null;
        }
      }

      return {
        session: state.session,
        mapData: state.mapData
      };
    } catch (error) {
      console.warn('Failed to load session from storage:', error);
      return null;
    }
  }

  /**
   * Clear saved session state from appropriate storage
   * @param {string} sessionType - 'demo' for sessionStorage, null/sessionId for localStorage
   */
  static clearSessionState(sessionType = null) {
    try {
      const storage = this.getStorage(sessionType);
      const key = this.getStorageKey(sessionType);
      storage.removeItem(key);
    } catch (error) {
      console.warn('Failed to clear session state:', error);
    }
  }

  /**
   * Check if we're on the home/landing page
   * @returns {boolean}
   */
  static isHome() {
    const params = new URLSearchParams(window.location.search);
    return params.get('session') === null;
  }
}
